// views/Settings/WalletCommissionRatesSettings.js
// The wallet service's fallback commission per vendor type (CommissionRate rows,
// walletService.calculateCommission). The wallet uses it only when an escrow
// hold arrives with no commission rate: orders that snapshot a rate (apps, web,
// POS, brand quotes, ticketing) never reach it; AI gateway and chat orders,
// which snapshot none, do. See help/commissionTaxHelp.js "defaultCommission".
import React, { useEffect, useState } from "react";
import { notify } from "../../utilities/notify";
import SettingsPanel from "../../components/SettingsPanel";
import { THEME as C } from "../../components/dashboardTheme";
import { apiWalletCommissionRates, apiWalletSetCommissionRate } from "../../api";
import { field, fmtWhen, ReadOnlyNotice, SaveButton } from "./settingsUi";
import SettingHelp from "./settingsHelp";

const VENDOR_TYPES = ["RESTAURANT", "RETAILER", "WHOLESALER", "HEALTHCARE", "BRAND", "BRAND_OWNER", "EVENT"];
const label = (t) => t.charAt(0) + t.slice(1).toLowerCase().replace("_", " ");

const WalletCommissionRatesSettings = ({ canEdit = true }) => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendorType, setVendorType] = useState("RESTAURANT");
  const [pct, setPct] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => apiWalletCommissionRates()
    .then(({ data }) => setRates(data.rates || []))
    .catch((err) => notify.fromError(err, "Could not load commission rates"))
    .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const current = rates.find((r) => r.vendorType === vendorType);
  const n = Number(pct);
  const valid = pct !== "" && Number.isFinite(n) && n >= 0 && n <= 100;

  const save = async (e) => {
    e.preventDefault();
    if (!valid) return notify.error("Enter a percentage between 0 and 100");
    setSaving(true);
    try {
      await apiWalletSetCommissionRate({ vendorType, commissionPercentage: n });
      notify.success(`${label(vendorType)} fallback commission is now ${n}%`, "The wallet applies it from now on to payments that arrive without a rate. Orders that carry a contract rate are not affected.");
      setPct("");
      await load();
    } catch (err) {
      notify.fromError(err, "Could not save the commission rate");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsPanel icon="fa-percent" title={<>Default commission by vendor type<SettingHelp topic="defaultCommission" /></>} subtitle="The wallet's fallback rate for payments that arrive without one. Most orders carry their own rate from the vendor's contract.">
      {!canEdit && <ReadOnlyNotice level="manage" />}
      <SettingsPanel.Section title="Current fallback rates" icon="fa-list">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>{["Vendor type", "Commission", "Effective from"].map((h) => (
              <th key={h} style={{ textAlign: "left", fontSize: 11, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.06em", padding: "6px 8px", borderBottom: `1px solid ${C.border}` }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {VENDOR_TYPES.map((t) => {
              const r = rates.find((x) => x.vendorType === t);
              return (
                <tr key={t}>
                  <td style={{ padding: "8px", fontWeight: 600, color: C.text }}>{label(t)}</td>
                  <td style={{ padding: "8px", color: r ? C.text : C.textLight }}>{r ? `${r.commissionPercentage}%` : loading ? "..." : "Not saved here; the wallet's built-in rate for this type applies"}</td>
                  <td style={{ padding: "8px", color: C.textLight, fontSize: 12 }}>{r ? fmtWhen(r.effectiveFrom) : ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </SettingsPanel.Section>
      {canEdit && (
        <form onSubmit={save}>
          <SettingsPanel.Section title="Set a fallback rate" icon="fa-pen">
            <div style={field.grid(2)}>
              <div>
                <label style={field.label}>Vendor type</label>
                <select style={field.input} value={vendorType} onChange={(e) => setVendorType(e.target.value)} disabled={saving}>
                  {VENDOR_TYPES.map((t) => <option key={t} value={t}>{label(t)}</option>)}
                </select>
              </div>
              <div>
                <label style={field.label}>Commission (%)<SettingHelp topic="defaultCommissionRate" /></label>
                <input style={field.input} type="number" min="0" max="100" step="0.5" value={pct} disabled={saving}
                  placeholder={current ? String(current.commissionPercentage) : ""} onChange={(e) => setPct(e.target.value)} />
                <div style={field.help}>Saving closes the previous rate with today's date. Escrow already held keeps the rate it was held at, and a vendor's own contract rate always wins over this one.</div>
              </div>
            </div>
            <div style={field.actions}><SaveButton saving={saving} disabled={!valid}>Set rate</SaveButton></div>
          </SettingsPanel.Section>
        </form>
      )}
    </SettingsPanel>
  );
};

export default WalletCommissionRatesSettings;
